import React, { useEffect, useState } from 'react';
import { landlordAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Building2, DoorOpen, Users, CreditCard, AlertCircle, TrendingUp, Crown, Loader2, Copy, Key, ShieldCheck, ArrowRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { LandlordOnboarding } from '../../components/onboarding/LandlordOnboarding';
import { toast } from 'sonner';

export default function LandlordDashboard() {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [resumeBannerDismissed, setResumeBannerDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
    checkOnboarding();
    // Refresh user data to get latest landlord_code if it was added after login
    refreshUser();
    // Restore dismissed banner state from localStorage
    if (user) {
      const dismissed = localStorage.getItem(`udorm_resume_banner_dismissed_${user.user_id}`);
      if (dismissed) setResumeBannerDismissed(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  const checkOnboarding = () => {
    // Check if landlord has completed onboarding (consolidated key v2)
    if (user) {
      const onboardingComplete = localStorage.getItem(`udorm_onboarding_v2_${user.user_id}`);
      if (!onboardingComplete) {
        // Small delay to let dashboard load first
        setTimeout(() => setShowOnboarding(true), 1500);
      }
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await landlordAPI.getDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const statCards = [
    {
      title: t('nav.properties'),
      value: stats?.total_properties || 0,
      icon: Building2,
      color: 'bg-[#C85A42]',
      onClick: () => navigate('/landlord/properties'),
    },
    {
      title: t('dashboard.totalUnits'),
      value: stats?.total_units || 0,
      icon: DoorOpen,
      color: 'bg-[#6B8E6B]',
      onClick: () => navigate('/landlord/units'),
    },
    {
      title: t('dashboard.activeLeases'),
      value: stats?.active_leases || 0,
      icon: Users,
      color: 'bg-indigo-500',
      onClick: () => navigate('/landlord/leases'),
    },
    {
      title: t('dashboard.monthlyRevenue'),
      value: formatCurrency(stats?.monthly_revenue || 0),
      icon: TrendingUp,
      color: 'bg-emerald-500',
      onClick: () => navigate('/landlord/reports'),
    },
  ];

  // Resume Setup banner — show only if onboarding is done AND subscription is missing/expired/lapsed AND banner not dismissed
  const subStatus = String(stats?.subscription?.status || '').toLowerCase();
  const subscribed = subStatus === 'active' || subStatus === 'trial' || subStatus === 'trialing';
  const onboardingFlag = user ? localStorage.getItem(`udorm_onboarding_v2_${user.user_id}`) : null;
  const showResumeBanner =
    !loading &&
    !showOnboarding &&
    !resumeBannerDismissed &&
    !subscribed &&
    !!onboardingFlag; // they completed/skipped onboarding but didn't subscribe

  const dismissBanner = () => {
    if (user) {
      localStorage.setItem(`udorm_resume_banner_dismissed_${user.user_id}`, 'true');
    }
    setResumeBannerDismissed(true);
  };

  return (
    <div className="space-y-6" data-testid="landlord-dashboard">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold">{t('nav.dashboard')}</h1>
        <p className="text-muted-foreground mt-1">{t('dashboard.welcomeBack')}! {t('dashboard.propertyOverview')}</p>
      </div>

      {/* Resume Setup Banner */}
      {showResumeBanner && (
        <div
          className="relative overflow-hidden rounded-2xl border-2 border-[#C85A42]/30 bg-gradient-to-r from-[#C85A42]/10 via-[#C85A42]/5 to-transparent dark:from-[#C85A42]/20 dark:via-[#C85A42]/10 p-5 sm:p-6 animate-fade-in"
          data-testid="resume-setup-banner"
        >
          <button
            type="button"
            onClick={dismissBanner}
            className="absolute top-3 right-3 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10"
            aria-label="Dismiss"
            data-testid="resume-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#C85A42] flex items-center justify-center flex-shrink-0">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-[#2F4F4F] dark:text-white">
                Finish setting up — pick a plan to unlock everything
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                You're one step away. Monthly $9.99 or Annual $95 (save ~20%). No auto-renew, cancel anytime.
              </p>
              <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800">
                <ShieldCheck className="w-3.5 h-3.5" />
                30-Day Money-Back Guarantee
              </div>
            </div>
            <Button
              onClick={() => navigate('/landlord/subscription')}
              className="bg-[#C85A42] hover:bg-[#B04A35] w-full sm:w-auto"
              data-testid="resume-banner-cta"
            >
              Pick a plan
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card
            key={stat.title}
            className="cursor-pointer card-hover animate-fade-in"
            style={{ animationDelay: `${index * 0.05}s` }}
            onClick={stat.onClick}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-2xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Occupancy */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DoorOpen className="w-5 h-5 text-[#C85A42]" />
              {t('dashboard.occupancyRate')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold">{stats?.occupancy_rate || 0}%</span>
                <Badge variant={stats?.occupancy_rate >= 80 ? 'default' : 'secondary'}>
                  {stats?.occupied_units || 0} / {stats?.total_units || 0} {t('nav.units').toLowerCase()}
                </Badge>
              </div>
              <Progress value={stats?.occupancy_rate || 0} className="h-3" />
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                  <p className="text-sm text-muted-foreground">{t('units.occupied')}</p>
                  <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{stats?.occupied_units || 0}</p>
                </div>
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <p className="text-sm text-muted-foreground">{t('units.vacant')}</p>
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{(stats?.total_units || 0) - (stats?.occupied_units || 0)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              {t('dashboard.pendingActions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.pending_tenants > 0 && (
                <div
                  className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950 rounded-lg cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors"
                  onClick={() => navigate('/landlord/tenants')}
                  data-testid="pending-tenants-action"
                >
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <span className="font-medium">{t('tenants.pendingVerifications')}</span>
                  </div>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">{stats.pending_tenants}</Badge>
                </div>
              )}

              {stats?.pending_payments > 0 && (
                <div
                  className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-950 rounded-lg cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900 transition-colors"
                  onClick={() => navigate('/landlord/payments')}
                  data-testid="pending-payments-action"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    <span className="font-medium">{t('payments.pendingPayments')}</span>
                  </div>
                  <Badge variant="destructive">{stats.pending_payments}</Badge>
                </div>
              )}

              {stats?.pending_payments === 0 && stats?.pending_tenants === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>{t('dashboard.noPendingActions')}</p>
                  <p className="text-sm">{t('messages.allCaughtUp')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Subscription Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              {t('subscription.yourSubscription')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.subscription ? (
              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <p className="text-sm text-muted-foreground">{t('subscription.currentPlan')}</p>
                  <p className="text-xl font-bold capitalize">{stats.subscription.tier}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('common.status')}</p>
                  <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300">{stats.subscription.status}</Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('nav.properties')}</p>
                  <p className="font-medium">
                    {stats.total_properties} / {stats.subscription.property_limit === -1 ? '∞' : stats.subscription.property_limit}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('nav.units')}</p>
                  <p className="font-medium">
                    {stats.total_units} / {stats.subscription.unit_limit === -1 ? '∞' : stats.subscription.unit_limit}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">{t('subscription.noActiveSubscription')}</p>
            )}
          </CardContent>
        </Card>

        {/* Landlord Code Card */}
        {user?.landlord_code && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-[#C85A42]" />
                {t('auth.landlordCode')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t('tenants.shareCodeDescription')}
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 px-4 py-3 bg-muted rounded-lg font-mono text-lg font-bold tracking-wider">
                    {user.landlord_code}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(user.landlord_code);
                      toast.success(t('common.copied') + '!');
                    }}
                    data-testid="copy-landlord-code"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Landlord Onboarding Dialog */}
      <LandlordOnboarding
        open={showOnboarding}
        onOpenChange={setShowOnboarding}
      />
    </div>
  );
}
